import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Notification } from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private socket!: WebSocket;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private isManualDisconnect = false;
  private reconnectTimeout: any;
  private pingInterval: any;

  // Track subscribed oppIds
  private subscribedOppIds = new Set<string>();

  // Event listeners
  private notificationCallbacks: Array<(eventType: string, notification: Notification) => void> = [];
  private notificationReadCallbacks: Array<(notificationId: number) => void> = [];

  private connectedSubject = new BehaviorSubject<boolean>(false);
  connected$ = this.connectedSubject.asObservable();

  constructor() {}

  // Get WebSocket URL - automatically detects environment
  private getWebSocketUrl(): string {
    // Use environment URL if available
    if (environment.wsUrl) {
      return environment.wsUrl;
    }

    // Auto-detect based on current host
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname === 'localhost' 
      ? 'localhost:3000'  // Development
      : 'api-dev.financelogix.com';  // Production
    
    return `${protocol}//${host}/ws`;
  }

  connect(): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    const wsUrl = this.getWebSocketUrl();
    this.isManualDisconnect = false;
    this.reconnectAttempts = 0;

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log(' WebSocket connected to', wsUrl);
        this.reconnectAttempts = 0;
        this.connectedSubject.next(true);
        
        // Resubscribe to all previously subscribed oppIds
        this.subscribedOppIds.forEach(oppId => {
          this.subscribe(oppId);
        });

        // Start ping interval
        this.startPingInterval();
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error(' Failed to parse WebSocket message:', error, event.data);
        }
      };

      this.socket.onerror = (error) => {
        console.error(' WebSocket error:', error);
        this.connectedSubject.next(false);
      };

      this.socket.onclose = (event) => {
        console.log('WebSocket disconnected', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        this.connectedSubject.next(false);
        this.stopPingInterval();
        
        // Only reconnect if it wasn't a clean close and not manual disconnect
        if (!this.isManualDisconnect && !event.wasClean) {
          this.attemptReconnect();
        }
      };
    } catch (error) {
      console.error(' Failed to create WebSocket connection:', error);
      this.connectedSubject.next(false);
      this.attemptReconnect();
    }
  }

  private handleMessage(data: any): void {
    console.log('WebSocket message received:', data);

    switch (data.type) {
      case 'connected':
        console.log(' Connection established');
        break;
      
      case 'subscribed':
        console.log(`Subscribed to oppId: ${data.oppId}`);
        break;
      
      case 'notification_created':
        console.log('New notification:', data.notification);
        this.notifyCallbacks('notification_created', data.notification);
        break;
      
      case 'notification_updated':
        console.log('Notification updated:', data.notification);
        this.notifyCallbacks('notification_updated', data.notification);
        break;
      
      case 'notification_read':
        const notificationId = data.notificationId || data.notification?.id || data.id;
        if (notificationId) {
          this.notificationReadCallbacks.forEach(callback => {
            try {
              callback(notificationId);
            } catch (err) {
              console.error('Error in notification_read callback:', err);
            }
          });
        }
        break;
      
      case 'pong':
        // Keep-alive response
        break;
      
      case 'error':
        console.error(' WebSocket error:', data.message);
        break;
      
      default:
        // Fallback: if it looks like a notification, handle it
        if (data.notification || (data.id && (data.title || data.message))) {
          const notification = data.notification || data;
          this.notifyCallbacks('notification_created', notification);
        } else {
          console.log('📨 Unknown message type:', data.type, data);
        }
    }
  }

  subscribe(oppId: string, userId?: string | null): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn(' WebSocket not connected. Will subscribe after connection.');
      this.subscribedOppIds.add(oppId);
      this.connect();
      return;
    }

    this.subscribedOppIds.add(oppId);
    
    const userIdToSend = userId || this.getUserId();
    
    this.send({
      type: 'subscribe',
      oppId: oppId,
      userId: userIdToSend
    });
  }

  unsubscribe(oppId: string): void {
    this.subscribedOppIds.delete(oppId);
    
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.send({
        type: 'unsubscribe',
        oppId: oppId
      });
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(` Max reconnection attempts (${this.maxReconnectAttempts}) reached`);
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    
    console.log(` Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private startPingInterval(): void {
    this.stopPingInterval(); // Clear any existing interval
    
    this.pingInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      }
    }, 30000); // Every 30 seconds
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private getUserId(): string | null {
    try {
      const jwtToken = localStorage.getItem('jwt_token');
      if (jwtToken) {
        // Try to decode JWT to get user ID (basic decode, not verified)
        const payload = JSON.parse(atob(jwtToken.split('.')[1]));
        return payload.sub || payload.userId || payload.id || null;
      }
    } catch (error) {
      console.warn(' Could not get user ID from token:', error);
    }
    return null;
  }

  onNotification(callback: (eventType: string, notification: Notification) => void): void {
    this.notificationCallbacks.push(callback);
  }

  onNotificationRead(callback: (notificationId: number) => void): void {
    this.notificationReadCallbacks.push(callback);
  }

  private notifyCallbacks(eventType: string, notification: Notification): void {
    this.notificationCallbacks.forEach(callback => {
      try {
        callback(eventType, notification);
      } catch (error) {
        console.error(' Error in notification callback:', error);
      }
    });
  }

  send(data: any): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.socket.send(message);
    } else {
      console.warn('WebSocket is not connected. Cannot send message.');
    }
  }

  disconnect(): void {
    this.isManualDisconnect = true;
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopPingInterval();

    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close(1000, 'Manual disconnect');
      }
      
      this.socket = null as any;
    }

    this.subscribedOppIds.clear();
    this.connectedSubject.next(false);
  }

  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.notificationCallbacks = [];
    this.notificationReadCallbacks = [];
  }
}
